import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { DecisionData } from '../types';

interface DecisionInputProps {
  onStart: (data: DecisionData) => void;
}

export function DecisionInput({ onStart }: DecisionInputProps) {
  const [decisionName, setDecisionName] = useState('');
  const [context, setContext] = useState('');
  const [successCriteria, setSuccessCriteria] = useState('');
  const [constraints, setConstraints] = useState('');
  const [deadlines, setDeadlines] = useState('');
  const [stakeholders, setStakeholders] = useState('');

  const canStart = decisionName.trim() !== '' && context.trim() !== '' && successCriteria.trim() !== '';

  const handleStart = () => {
    onStart({
      decisionName: decisionName.trim(),
      context: context.trim(),
      successCriteria: successCriteria.trim(),
      constraints: constraints.trim() || undefined,
      deadlines: deadlines.trim() || undefined,
      stakeholders: stakeholders.trim() || undefined,
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">
          Clarify the Decision
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          What decision are you facing, and what does 'success' look like?
        </p>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Decision Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={decisionName}
              onChange={(e) => setDecisionName(e.target.value)}
              placeholder="e.g., Choose a new CRM system"
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Context <span className="text-[var(--coral)]">*</span>
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Describe the situation, background, and why this decision matters..."
              rows={4}
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Success Criteria <span className="text-[var(--coral)]">*</span>
            </label>
            <textarea
              value={successCriteria}
              onChange={(e) => setSuccessCriteria(e.target.value)}
              placeholder="What does success look like? How will you know you made the right decision?"
              rows={3}
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Constraints (optional)
            </label>
            <input
              type="text"
              value={constraints}
              onChange={(e) => setConstraints(e.target.value)}
              placeholder="e.g., Budget limit, technical requirements..."
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Deadlines (optional)
            </label>
            <input
              type="text"
              value={deadlines}
              onChange={(e) => setDeadlines(e.target.value)}
              placeholder="e.g., Must decide by end of Q1"
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Stakeholders (optional)
            </label>
            <input
              type="text"
              value={stakeholders}
              onChange={(e) => setStakeholders(e.target.value)}
              placeholder="e.g., Engineering team, Sales, Executive leadership"
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <button
          onClick={handleStart}
          disabled={!canStart}
          className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed font-medium text-lg transition-colors flex items-center justify-center gap-2"
        >
          Start Assessment
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}


